import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, User, Shield, UserCheck, X, Mail, Lock } from 'lucide-react';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'dispatcher' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = () => {
    fetch('/api/users')
      .then(res => res.json())
      .then(data => setUsers(data))
      .catch(error => console.error('Ошибка загрузки пользователей:', error));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
    const method = editingUser ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
    .then(res => {
      if (!res.ok) throw new Error('Ошибка при сохранении');
      return res.json();
    })
    .then(() => {
      fetchUsers();
      closeModal();
      alert(editingUser ? 'Пользователь обновлён!' : 'Пользователь создан!');
    })
    .catch(error => alert('Ошибка: ' + error.message));
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role });
    setShowModal(true);
  };

  const handleDelete = (userId) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      fetch(`/api/users/${userId}`, { method: 'DELETE' })
        .then(res => {
          if (!res.ok) throw new Error('Ошибка при удалении');
          return res.json();
        })
        .then(() => { fetchUsers(); alert('Пользователь удалён!'); })
        .catch(error => alert('Ошибка: ' + error.message));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'dispatcher' });
  };

  const getRoleInfo = (role) => {
    if (role === 'doctor') return { label: 'Врач', icon: UserCheck, color: 'bg-green-100 text-green-800', iconColor: 'text-green-600' };
    if (role === 'admin') return { label: 'Администратор', icon: Shield, color: 'bg-purple-100 text-purple-800', iconColor: 'text-purple-600' };
    return { label: 'Диспетчер', icon: Shield, color: 'bg-blue-100 text-blue-800', iconColor: 'text-blue-600' };
  };

  return (
    <div className="space-y-5">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Пользователи</h1>
          <p className="text-slate-500 mt-0.5">Управление диспетчерами и врачами</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Добавить пользователя</span>
        </button>
      </div>

      {/* Список пользователей */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">
            Список пользователей
            <span className="ml-2 text-sm font-normal text-slate-500">({users.length})</span>
          </h2>
        </div>

        {users.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <User className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Пользователей нет</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((user) => {
              const roleInfo = getRoleInfo(user.role);
              const RoleIcon = roleInfo.icon;
              return (
                <div key={user.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-4 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-6 w-6 text-slate-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-slate-900">{user.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="flex items-center space-x-1 text-sm text-slate-500">
                            <Mail className="h-3.5 w-3.5" />
                            <span>{user.email}</span>
                          </span>
                          <span className={`flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${roleInfo.color}`}>
                            <RoleIcon className="h-3.5 w-3.5" />
                            <span>{roleInfo.label}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(user)}
                        className="flex items-center space-x-1.5 px-3 py-2 bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="hidden sm:inline">Изменить</span>
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="flex items-center space-x-1.5 px-3 py-2 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="hidden sm:inline">Удалить</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-y-auto max-h-[92vh] shadow-2xl">
            <div className="bg-blue-800 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}
              </h3>
              <button onClick={closeModal} className="text-blue-300 hover:text-white p-1 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Полное имя *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Фамилия Имя Отчество"
                  required
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">
                  <span className="flex items-center space-x-1.5"><Mail className="h-4 w-4" /><span>Email *</span></span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">
                  <span className="flex items-center space-x-1.5"><Lock className="h-4 w-4" /><span>{editingUser ? 'Новый пароль (необязательно)' : 'Пароль *'}</span></span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder={editingUser ? 'Оставьте пустым, чтобы не менять' : 'Введите пароль'}
                  required={!editingUser}
                />
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Роль *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'dispatcher', label: 'Диспетчер', color: 'border-blue-500 bg-blue-50' },
                    { value: 'doctor', label: 'Врач', color: 'border-green-500 bg-green-50' }
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${formData.role === opt.value ? opt.color : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="role"
                        value={opt.value}
                        checked={formData.role === opt.value}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="mr-2.5 h-4 w-4"
                      />
                      <span className="text-sm font-semibold text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3.5 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
                >
                  {editingUser ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
